<template lang="pug">
//- mobile-nav-bar--hidden: панель прячется, пока открыта экранная клавиатура —
//- иначе она висит над клавиатурой и съедает часть и без того малого экрана.
nav.v-mobile-nav-bar.mobile-nav-bar(v-if="data" :class="{'mobile-nav-bar--hidden': keyboardOpen}")
	ul.mobile-nav-bar__list(v-if="data.links" )
		li.mobile-nav-bar__item(v-for="(link, index) in data.links" :key="index" :class="{'active': isActive(link, index), 'mobile-nav-bar__item--has-trigger': link.lkTrigger}")
			//- Пункт «Профиль»: клик по всему пункту (иконка + стрелка + текст)
			//- открывает шторку личного кабинета снизу вверх. Стрелка — справа от иконки.
			button.mobile-nav-bar__link.mobile-nav-bar__link--trigger(v-if="link.lkTrigger" type="button" aria-label="Меню кабинета" @click="openLkMobileMenu")
				.mobile-nav-bar__icon-wrap
					v-svg-icon.mobile-nav-bar__icon(v-if="link.icon" :icon="link.icon")
					v-svg-icon.mobile-nav-bar__trigger-icon(icon="chevron-down-sm")
				.mobile-nav-bar__text(v-if="link.text" ) {{ link.text }}
			a.mobile-nav-bar__link(v-else-if="link.link" :href="link.link" @click="onLinkClick(index)")
				v-svg-icon.mobile-nav-bar__icon(v-if="link.icon" :icon="link.icon")
				.mobile-nav-bar__text(v-if="link.text" ) {{ link.text }}
</template>

<script>

import sourceMixin from "../components/mixins/v-mixin-source.vue";
import controller from "../scripts/controller/controller.js";

export default {
	mixins: [
		sourceMixin
	],

	data() {
		return {
			data: null,
			// индекс пункта, по которому только что тапнули (подсветка до
			// перехода на новую страницу — :active на тачах ненадёжен)
			clickedIndex: null,
			// открыта ли пользовательская шторка ЛК (для подсветки «Профиль»)
			lkOpen: false,
			// открыта ли экранная клавиатура (панель на это время прячется)
			keyboardOpen: false,
			// в фокусе ли поле ввода текста
			textFieldFocused: false,
			// высота вьюпорта без клавиатуры — база для сравнения
			baseViewportHeight: 0,
		};
	},

	created() {
		controller.events.on('lk-nav-open', this.onLkOpen);
		controller.events.on('lk-nav-close', this.onLkClose);
	},

	mounted() {
		this.baseViewportHeight = this.getViewportHeight();

		// Клавиатура появляется только при фокусе в текстовое поле, поэтому
		// focusin/focusout — основной сигнал; visualViewport лишь уточняет его
		// (см. updateKeyboardState).
		document.addEventListener('focusin', this.onFocusIn);
		document.addEventListener('focusout', this.onFocusOut);

		if (window.visualViewport) {
			window.visualViewport.addEventListener('resize', this.onViewportResize);
		}
	},

	unmounted() {
		controller.events.off('lk-nav-open', this.onLkOpen);
		controller.events.off('lk-nav-close', this.onLkClose);

		document.removeEventListener('focusin', this.onFocusIn);
		document.removeEventListener('focusout', this.onFocusOut);

		if (window.visualViewport) {
			window.visualViewport.removeEventListener('resize', this.onViewportResize);
		}
	},

	methods: {
		dataSet(data) {
			this.data = data;
		},

		// Пункт активен: по текущей странице (link.active), либо по свежему
		// тапу (clickedIndex), либо «Профиль» — пока открыта шторка ЛК.
		isActive(link, index) {
			if (link.lkTrigger) {
				return this.lkOpen;
			}
			return !!link.active || this.clickedIndex === index;
		},

		onLinkClick(index) {
			// подсвечиваем сразу по тапу, до ухода на новую страницу
			this.clickedIndex = index;
		},

		onLkOpen() {
			this.lkOpen = true;
		},

		onLkClose() {
			this.lkOpen = false;
		},

		openLkMobileMenu() {
			// повторный клик по той же кнопке закрывает шторку
			controller.toggleLkMobileMenu();
		},

		getViewportHeight() {
			return window.visualViewport ? window.visualViewport.height : window.innerHeight;
		},

		// Текстовое ли поле. Список типов — чёрный, а не белый: в формах проекта
		// у инпутов встречаются нестандартные type (например "promocode"), и по
		// белому списку они бы не распознались.
		isTextField(element) {
			if (!element) {
				return false;
			}
			if (element.isContentEditable) {
				return true;
			}

			const tag = (element.tagName || '').toLowerCase();
			if (tag === 'textarea') {
				return true;
			}
			if (tag !== 'input') {
				return false;
			}

			const type = (element.getAttribute('type') || 'text').toLowerCase();

			return ['checkbox', 'radio', 'button', 'submit', 'reset', 'file', 'range', 'color', 'image', 'hidden']
				.indexOf(type) < 0;
		},

		onFocusIn(e) {
			this.textFieldFocused = this.isTextField(e.target);
			this.updateKeyboardState();
		},

		onFocusOut() {
			this.textFieldFocused = false;
			this.updateKeyboardState();
		},

		onViewportResize() {
			// Пока поле не в фокусе, текущая высота и есть «база»: так учитываются
			// поворот экрана и сворачивание адресной строки браузера.
			if (!this.textFieldFocused) {
				this.baseViewportHeight = this.getViewportHeight();
			}
			this.updateKeyboardState();
		},

		updateKeyboardState() {
			if (!this.textFieldFocused) {
				this.keyboardOpen = false;
				return;
			}

			// Без visualViewport измерить нечем — доверяем факту фокуса.
			if (!window.visualViewport) {
				this.keyboardOpen = true;
				return;
			}

			// Сравниваем с высотой ДО фокуса, а не с window.innerHeight: на Android
			// клавиатура ужимает и layout-вьюпорт, поэтому разница между innerHeight
			// и visualViewport там остаётся около нуля и ничего не показала бы.
			// Порог отсекает мелочь вроде схлопывания адресной строки, а заодно
			// случай аппаратной клавиатуры (фокус есть, вьюпорт не изменился).
			this.keyboardOpen = (this.baseViewportHeight - this.getViewportHeight()) > 150;
		},

	}
};
</script>
